import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useContent } from "@/lib/useContent";
import { trackEvent } from "@/lib/analytics";
import { useElectionPhase, PopupCountdown, ElectionDayBanner } from "@/components/ElectionPhase";
import { ResultsBoard } from "@/components/ResultsBoard";

const AMOUNTS = [5, 10, 25, 50, 100];
const SESSION_KEY = "satish_donate_popup_seen";

const OpenSiteButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="mt-5 w-full flex items-center justify-center gap-2 rounded-full bg-brightblue text-white ring-2 ring-white font-extrabold text-base sm:text-lg py-3.5 shadow-lg hover:bg-brightblue-hover hover:-translate-y-0.5 transition-[transform,background-color]"
    data-testid="donate-popup-close"
  >
    Open Website <ArrowRight className="w-5 h-5" />
  </button>
);

const DonateButtons = ({ go }) => (
  <div className="grid grid-cols-3 gap-2 sm:gap-2.5" data-testid="donate-popup-amounts">
    {[...AMOUNTS, "other"].map((a) => (
      <button key={a} onClick={() => go(a)}
        className={`rounded-xl bg-white text-navy border-2 border-white font-serif font-extrabold py-2.5 sm:py-3 hover:bg-blue-50 hover:-translate-y-0.5 transition-[transform,background-color] ${a === "other" ? "text-sm uppercase" : "text-lg sm:text-xl"}`}
        data-testid={`donate-popup-amount-${a}`}>
        {a === "other" ? "Other" : `$${a}`}
      </button>
    ))}
  </div>
);

export const DonatePopup = () => {
  const { data: content } = useContent();
  const [open, setOpen] = useState(false);
  const [flyerOk, setFlyerOk] = useState(true);
  const reduce = useReducedMotion();
  const { phase, now } = useElectionPhase();
  const base = content?.donate?.actblue_url || "https://secure.actblue.com/donate/satish-chapagain";

  const { pathname } = useLocation();
  const results = phase === "results";

  useEffect(() => {
    if (results ? pathname === "/" : !sessionStorage.getItem(SESSION_KEY)) setOpen(true);
  }, [results, pathname]);

  const close = () => { sessionStorage.setItem(SESSION_KEY, "1"); setOpen(false); };
  const go = (amt) => {
    trackEvent("donate_click", { location: "popup", amount: amt });
    window.open(amt === "other" ? base : `${base}?amount=${amt}`, "_blank", "noopener,noreferrer");
    close();
  };
  const showFlyer = !results && flyerOk;

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="donate-popup-overlay">
          <div className="absolute inset-0 bg-navy-dark/75 backdrop-blur-sm" onClick={close} />
          <motion.div
            className={`relative w-full ${showFlyer ? "max-w-4xl md:grid-cols-2" : "max-w-xl"} max-h-[94vh] overflow-y-auto hero-gradient rounded-3xl shadow-2xl border border-white/10 grid`}
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 10 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            role="dialog" aria-modal="true" aria-label="Satish Chapagain for Delegate" data-testid="donate-popup">
            {showFlyer && (
              <img src="/popup-flyer.webp" alt="Vote Satish Chapagain, Democrat for Delegate 7A, Tuesday Nov 3"
                width="900" height="900" fetchpriority="high" decoding="async" onError={() => setFlyerOk(false)}
                className="w-full aspect-square object-contain bg-white max-h-[40vh] md:max-h-none" data-testid="popup-flyer" />
            )}
            <div className="p-5 sm:p-7 flex flex-col justify-center text-center">
              {results ? (
                <>
                  <h2 className="font-serif text-3xl sm:text-4xl font-extrabold text-white mb-5" data-testid="popup-thank-you">Thank you for voting!</h2>
                  <ResultsBoard />
                </>
              ) : (
                <>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200 mb-3">
                    {phase === "election_day" ? "Today is the day" : "Countdown to Election Day · Nov 3"}
                  </div>
                  {phase === "election_day" ? <ElectionDayBanner /> : <PopupCountdown now={now} />}
                  <h2 className="font-serif text-xl sm:text-2xl font-extrabold text-white mt-5 mb-3 uppercase">Support Satish Today</h2>
                  <DonateButtons go={go} />
                  <p className="mt-2.5 text-[11px] text-blue-200/80">Secure contributions processed by ActBlue.</p>
                </>
              )}
              <OpenSiteButton onClick={close} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
